import { Input, type SelectOption, Select } from "@clintonelec/react-storybook";
import type { FourthStream, CodecSettingsState } from "Interfaces";
import { Fragment } from "react/jsx-runtime";
import {
	bitrateControlOptions, codecOptions, firstStreamResolutionOptions, frameRateOptions, gopSizeOptions,
	imageQualityOptions, profileOptions, secondStreamResolutionOptions, thirdStreamResolutionOptions
} from "Data/Objects/CodecSettingsOptions";
import { BitrateControl, Codec, type ImageQuality, type Profile } from "Data/Objects/Camera";
import { type Dispatch, memo, type SetStateAction } from "react";
import { produce } from "immer";

type TabContentProps = {
	codecSettingsState: [ CodecSettingsState, Dispatch<SetStateAction<CodecSettingsState>> ];
	streamIndex: number;
};

function TabContent(props: TabContentProps) {
	const { codecSettingsState, streamIndex } = props;
	const [ localCodecSettings, setLocalCodecSettings ] = codecSettingsState;
	const {
		codec, profile, resolution, frameRate, gopSize, bitrateControl, bitrate, imageQuality, mjpegQuality
	} = localCodecSettings.streams[ streamIndex ];

	const isMJPEG = codec === Codec.MJPEG;

	const handleCodecSelect = (newCodec: string | null) => {
		setLocalCodecSettings(produce(localCodecSettings, draft => {
			if (!newCodec) {
				return;
			}

			draft.streams[ streamIndex ].codec = newCodec as Codec;
		}));
	};

	const handleProfileSelect = (newProfile: string | null) => {
		setLocalCodecSettings(produce(localCodecSettings, draft => {
			if (!newProfile) {
				return;
			}

			draft.streams[ streamIndex ].profile = newProfile as Profile;
		}));
	};

	const handleResolutionSelect = (newResolution: string | null) => {
		setLocalCodecSettings(produce(localCodecSettings, draft => {
			if (!newResolution) {
				return;
			}

			draft.streams[ streamIndex ].resolution = newResolution;
		}));
	};

	const handleFrameRateSelect = (newFrameRate: string | null) => {
		const newState = produce(localCodecSettings, draft => {
			if (!newFrameRate) {
				return;
			}

			draft.streams[ streamIndex ].frameRate = +newFrameRate;

			if (streamIndex !== 3) {
				draft.streams[ streamIndex ].gopSize = +newFrameRate;
			}

			const fourthStream = draft.streams[ 3 ] as FourthStream;

			if((fourthStream.useStreamSettings === "mainStream" && streamIndex === 0)
				|| (fourthStream.useStreamSettings === "secondStream" && streamIndex === 1)) {
				draft.streams[ 3 ].frameRate = +newFrameRate * fourthStream.frameRateLabel;
			}
		});

		setLocalCodecSettings(newState);
	};

	const handleGopSizeSelect = (newGopSize: string | null) => {
		setLocalCodecSettings(produce(localCodecSettings, draft => {
			if (!newGopSize) {
				return;
			}

			draft.streams[ streamIndex ].gopSize = +newGopSize;
		}));
	};

	const handleBitrateControlSelect = (newBitrateControl: string | null) => {
		setLocalCodecSettings(produce(localCodecSettings, draft => {
			if (!newBitrateControl) {
				return;
			}

			draft.streams[ streamIndex ].bitrateControl = newBitrateControl as BitrateControl;
		}));
	};

	const handleBitrateSave = (newBitrate: string) => {
		setLocalCodecSettings(produce(localCodecSettings, draft => {
			draft.streams[ streamIndex ].bitrate = +newBitrate;
		}));
	};

	const handleImageQualitySelect = (newImageQuality: string | null) => {
		setLocalCodecSettings(produce(localCodecSettings, draft => {
			if (!newImageQuality) {
				return;
			}

			draft.streams[ streamIndex ].imageQuality = newImageQuality as ImageQuality;
		}));
	};

	const handleMjpegQualitySave = (newMjpegQuality: string) => {
		setLocalCodecSettings(produce(localCodecSettings, draft => {
			draft.streams[ streamIndex ].mjpegQuality = +newMjpegQuality;
		}));
	};

	let resolutionOptions: SelectOption[] = [];

	switch (streamIndex) {
		case 0:
			resolutionOptions = firstStreamResolutionOptions;

			break;

		case 1:
			resolutionOptions = secondStreamResolutionOptions;

			break;

		case 2:
			resolutionOptions = thirdStreamResolutionOptions;

			break;
	}

	return (
		<Fragment>
			<div className="form-row">
				<span>Codec</span>
				<Select
					allowClear={ false }
					className="form-select"
					onSelect={ handleCodecSelect }
					options={ codecOptions }
					value={ codec }
				/>
			</div>
			<div className="form-row">
				<span>Profile</span>
				<Select
					allowClear={ false }
					className="form-select"
					onSelect={ handleProfileSelect }
					options={ profileOptions }
					value={ profile }
				/>
			</div>
			<div className="form-row">
				<span>Resolution</span>
				<Select
					allowClear={ false }
					className="form-select"
					onSelect={ handleResolutionSelect }
					options={ resolutionOptions }
					value={ resolution }
				/>
			</div>
			<div className="form-row">
				<span>Frame Rate</span>
				<Select
					allowClear={ false }
					className="form-select"
					onSelect={ handleFrameRateSelect }
					options={ frameRateOptions }
					value={ frameRate }
				/>
			</div>
			<div className="form-row">
				<span>GOP Size</span>
				<Select
					allowClear={ false }
					className="form-select"
					disabled={ isMJPEG && streamIndex === 2 }
					onSelect={ handleGopSizeSelect }
					options={ gopSizeOptions }
					value={ gopSize }
				/>
			</div>
			<div className="form-row">
				<span>Bitrate Control</span>
				<Select
					allowClear={ false }
					className="form-select"
					disabled={ isMJPEG && streamIndex === 2 }
					onSelect={ handleBitrateControlSelect }
					options={ bitrateControlOptions }
					value={ bitrateControl }
				/>
			</div>
			<div className="form-row">
				<span>Bitrate</span>
				<div className="codec-input-box">
					<Input
						hideCharMessage={ true }
						max={ 8000 }
						min={ 512 }
						onSave={ handleBitrateSave }
						type="number"
						value={ bitrate }
					/>
					<span>kbps (512 ~ 8000)</span>
				</div>
			</div>
			<div className="form-row">
				<span>Image Quality</span>
				<Select
					allowClear={ false }
					className="form-select"
					disabled={ bitrateControl === BitrateControl.CBR }
					onSelect={ handleImageQualitySelect }
					options={ imageQualityOptions }
					value={ imageQuality }
				/>
			</div>
			<div className="form-row">
				<span>MJPG Quality</span>
				<div className="codec-input-box">
					<Input
						disabled={ !isMJPEG }
						hideCharMessage={ true }
						max={ 100 }
						min={ 0 }
						onSave={ handleMjpegQualitySave }
						type="number"
						value={ mjpegQuality }
					/>
					<span>(0 ~ 100)</span>
				</div>
			</div>
		</Fragment>
	);
}

export default memo(TabContent);
